import React, { useMemo, useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import Breadcrumb from '../../components/common/Breadcrumb';
import ReactDataTable from 'react-data-table-component';
import { useAuth } from '../../contexts/AuthContext';
import {
    useLeaveBalances,
    useAdjustEmployeeBalance,
    useAdjustmentHistory,
    useBulkSetBalance,
    useSeedMissingBalances,
} from '../../hooks/useLeaveBalance';
import { useLeaveTypes } from '../../hooks/useLeaveTypes';
import { useMeDashboard } from '../../hooks/useDashboard';
import { IconLoader2, IconAdjustments, IconHistory, IconRefresh, IconUsersGroup } from '@tabler/icons-react';

const COLORS = [
    'var(--chart-color1)',
    'var(--chart-color2)',
    'var(--chart-color3)',
    'var(--chart-color4)',
    'var(--chart-color5)',
];

const REASONS = [
    { value: 'carry_over', label: 'Carry Over' },
    { value: 'manual_correction', label: 'Manual Correction' },
    { value: 'bonus', label: 'Bonus' },
    { value: 'deduction', label: 'Deduction' },
];

// ── Adjust Balance Modal
function AdjustBalanceModal({ isOpen, onClose, employee, leaveTypes, orgId }) {
    const [leaveTypeId, setLeaveTypeId] = useState('');
    const [balanceDays, setBalanceDays] = useState('');
    const [reason, setReason] = useState('');
    const [note, setNote] = useState('');

    const adjust = useAdjustEmployeeBalance();

    const reset = () => {
        setLeaveTypeId(''); setBalanceDays(''); setReason(''); setNote('');
    };

    const handleClose = () => { reset(); onClose(); };

    const handleSubmit = () => {
        if (!leaveTypeId || !balanceDays || !reason) {
            toast.error('Please fill all required fields');
            return;
        }
        adjust.mutate({
            data: {
                employee_id: employee.id,
                leave_type_id: leaveTypeId,
                balance_days: parseFloat(balanceDays),
                mode: 'add',
                reason,
                note: note || undefined,
            },
            orgId,
        }, {
            onSuccess: (res) => {
                toast.success(res?.message || 'Balance updated successfully');
                handleClose();
            },
            onError: (err) => {
                const d = err?.response?.data;
                toast.error(d?.error || d?.detail || 'Failed to adjust balance');
            },
        });
    };

    if (!isOpen || !employee) return null;

    return (
        <>
            <div className='fixed p-15 w-full max-w-[520px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]'>
                <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-light'>
                    <h5 className='text-[16px]/[19.2px] font-medium md:px-10 px-[7px] py-10 mb-20 text-center border-b border-border'>
                        Adjust Leave Balance
                    </h5>
                    <div className='my-10 md:px-10 px-[7px] max-h-[60vh] overflow-auto cus-scrollbar'>
                        {/* Employee info */}
                        <div className='flex items-center gap-10 p-12 bg-bg-light rounded-lg border border-border mb-20'>
                            {(() => {
                                const colors = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4'];
                                const initials = employee.name
                                    ? employee.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                                    : '?';
                                const bg = colors[(employee.name?.charCodeAt(0) || 0) % colors.length];
                                return (
                                    <div style={{ backgroundColor: bg }} className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                                        {initials}
                                    </div>
                                );
                            })()}
                            <div>
                                <p className='text-sm font-medium leading-none mb-2'>{employee.name}</p>
                                <p className='text-xs text-text-secondary'>{employee.title}</p>
                            </div>
                        </div>
                        <div className='grid grid-cols-12 gap-20'>
                            <div className='form-group col-span-12'>
                                <label className='text-sm mb-2 block'>Leave Type *</label>
                                <select
                                    value={leaveTypeId}
                                    onChange={e => setLeaveTypeId(e.target.value)}
                                    className='w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary'
                                >
                                    <option value=''>-- Select Leave Type --</option>
                                    {leaveTypes.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className='form-group col-span-6'>
                                <label className='text-sm mb-2 block'>Days *</label>
                                <input
                                    type='number'
                                    min='0'
                                    step='0.5'
                                    value={balanceDays}
                                    onChange={e => setBalanceDays(e.target.value)}
                                    placeholder='e.g. 5'
                                    className='w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary'
                                />
                            </div>
                            <div className='form-group col-span-6'>
                                <label className='text-sm mb-2 block'>Reason *</label>
                                <select
                                    value={reason}
                                    onChange={e => setReason(e.target.value)}
                                    className='w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary'
                                >
                                    <option value=''>-- Select Reason --</option>
                                    {REASONS.map(r => (
                                        <option key={r.value} value={r.value}>{r.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className='form-group col-span-12'>
                                <label className='text-sm mb-2 block'>Note <span className='text-text-secondary'>(optional)</span></label>
                                <textarea
                                    rows={3}
                                    value={note}
                                    onChange={e => setNote(e.target.value)}
                                    placeholder='e.g. 5 days carried over from FY2024 unused balance'
                                    className='w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary resize-none'
                                />
                            </div>
                        </div>
                    </div>
                    <div className='md:px-10 px-[7px] flex gap-10 justify-end py-10'>
                        <button onClick={handleClose} className='btn btn-outline-secondary'>Cancel</button>
                        <button onClick={handleSubmit} className='btn btn-primary' disabled={adjust.isPending}>
                            {adjust.isPending ? 'Saving...' : 'Save Adjustment'}
                        </button>
                    </div>
                </div>
            </div>
            <div onClick={handleClose} className='fixed z-[5] w-full h-full left-0 top-0 bg-bg-transparent backdrop-blur-[5px]'></div>
        </>
    );
}

// ── Bulk Set Modal
function BulkSetModal({ isOpen, onClose, leaveTypes, orgId }) {
    const [leaveTypeId, setLeaveTypeId] = useState('');
    const [balanceDays, setBalanceDays] = useState('');

    const bulkSet = useBulkSetBalance();

    const reset = () => { setLeaveTypeId(''); setBalanceDays(''); };
    const handleClose = () => { reset(); onClose(); };

    const handleSubmit = () => {
        if (!leaveTypeId || !balanceDays) {
            toast.error('Please select a leave type and enter balance days');
            return;
        }
        bulkSet.mutate({
            data: { leave_type_id: leaveTypeId, balance_days: parseFloat(balanceDays) },
            orgId,
        }, {
            onSuccess: (res) => {
                toast.success(res?.message || 'Bulk balance set successfully');
                handleClose();
            },
            onError: (err) => {
                const d = err?.response?.data;
                toast.error(d?.error || 'Failed to set balances');
            },
        });
    };

    if (!isOpen) return null;

    return (
        <>
            <div className='fixed p-15 w-full max-w-[440px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]'>
                <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-light'>
                    <h5 className='text-[16px]/[19.2px] font-medium md:px-10 px-[7px] py-10 mb-20 text-center border-b border-border'>
                        Bulk Set Balance
                    </h5>
                    <div className='my-10 md:px-10 px-[7px]'>
                        <p className='text-sm text-text-secondary mb-16'>
                            This will overwrite the balance for <strong>all employees</strong> for the selected leave type.
                        </p>
                        <div className='grid grid-cols-12 gap-20'>
                            <div className='form-group col-span-12'>
                                <label className='text-sm mb-2 block'>Leave Type *</label>
                                <select
                                    value={leaveTypeId}
                                    onChange={e => setLeaveTypeId(e.target.value)}
                                    className='w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary'
                                >
                                    <option value=''>-- Select Leave Type --</option>
                                    {leaveTypes.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className='form-group col-span-12'>
                                <label className='text-sm mb-2 block'>Balance Days *</label>
                                <input
                                    type='number'
                                    min='0'
                                    step='0.5'
                                    value={balanceDays}
                                    onChange={e => setBalanceDays(e.target.value)}
                                    placeholder='e.g. 21'
                                    className='w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary'
                                />
                            </div>
                        </div>
                    </div>
                    <div className='md:px-10 px-[7px] flex gap-10 justify-end py-10'>
                        <button onClick={handleClose} className='btn btn-outline-secondary'>Cancel</button>
                        <button onClick={handleSubmit} className='btn btn-primary' disabled={bulkSet.isPending}>
                            {bulkSet.isPending ? 'Setting...' : 'Set for All Employees'}
                        </button>
                    </div>
                </div>
            </div>
            <div onClick={handleClose} className='fixed z-[5] w-full h-full left-0 top-0 bg-bg-transparent backdrop-blur-[5px]'></div>
        </>
    );
}

// ── Adjustment History Modal
function HistoryModal({ isOpen, onClose, orgId, employees, leaveTypes }) {
    const [filterEmployee, setFilterEmployee] = useState('');
    const [filterLeaveType, setFilterLeaveType] = useState('');

    const filters = {};
    if (filterEmployee) filters.employee_id = filterEmployee;
    if (filterLeaveType) filters.leave_type_id = filterLeaveType;

    const { data: history = [], isLoading } = useAdjustmentHistory(isOpen ? orgId : null, filters);

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

    if (!isOpen) return null;

    return (
        <>
            <div className='fixed p-15 w-full max-w-[820px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]'>
                <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-light'>
                    <h5 className='text-[16px]/[19.2px] font-medium md:px-10 px-[7px] py-10 mb-20 text-center border-b border-border'>
                        Balance Adjustment History
                    </h5>

                    {/* Filters */}
                    <div className='md:px-10 px-[7px] flex flex-wrap gap-10 mb-16'>
                        <div className='form-group'>
                            <select
                                value={filterEmployee}
                                onChange={e => setFilterEmployee(e.target.value)}
                            >
                                <option value=''>All Employees</option>
                                {employees.map(e => (
                                    <option key={e.id} value={e.id}>{e.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className='form-group'>
                            <select
                                value={filterLeaveType}
                                onChange={e => setFilterLeaveType(e.target.value)}
                            >
                                <option value=''>All Leave Types</option>
                                {leaveTypes.map(t => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className='my-10 md:px-10 px-[7px] max-h-[55vh] overflow-auto cus-scrollbar'>
                        {isLoading ? (
                            <div className="flex items-center justify-center py-30">
                                <IconLoader2 size={24} className="animate-spin text-primary" />
                            </div>
                        ) : history.length === 0 ? (
                            <p className="text-sm text-text-secondary text-center py-30">No adjustment records found.</p>
                        ) : (
                            <table className="w-full min-w-[600px] text-sm">
                                <thead>
                                    <tr className="border-b border-border">
                                        <th className="text-left p-8 text-xs text-text-secondary font-medium">Employee</th>
                                        <th className="text-left p-8 text-xs text-text-secondary font-medium">Leave Type</th>
                                        <th className="text-center p-8 text-xs text-text-secondary font-medium">Before</th>
                                        <th className="text-center p-8 text-xs text-text-secondary font-medium">After</th>
                                        <th className="text-left p-8 text-xs text-text-secondary font-medium">Reason</th>
                                        <th className="text-left p-8 text-xs text-text-secondary font-medium">Adjusted By</th>
                                        <th className="text-left p-8 text-xs text-text-secondary font-medium">Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((row) => {
                                        const diff = parseFloat(row.days_after) - parseFloat(row.days_before);
                                        return (
                                            <tr key={row.id} className="border-b border-border hover:bg-bg-light">
                                                <td className="p-8 font-medium">{row.employee_name}</td>
                                                <td className="p-8 text-text-secondary">{row.leave_type}</td>
                                                <td className="p-8 text-center">{parseFloat(row.days_before).toFixed(1)}</td>
                                                <td className="p-8 text-center font-semibold">
                                                    <span className={diff >= 0 ? 'text-success' : 'text-danger'}>
                                                        {parseFloat(row.days_after).toFixed(1)}
                                                        <span className="ml-4 text-xs font-normal">
                                                            ({diff >= 0 ? '+' : ''}{diff.toFixed(1)})
                                                        </span>
                                                    </span>
                                                </td>
                                                <td className="p-8">
                                                    <span className="badge badge-secondary capitalize">{row.reason?.replace('_', ' ')}</span>
                                                </td>
                                                <td className="p-8 text-text-secondary">{row.adjusted_by}</td>
                                                <td className="p-8 text-text-secondary whitespace-nowrap">{formatDate(row.adjusted_at)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className='md:px-10 px-[7px] flex gap-10 justify-end py-10'>
                        <button onClick={onClose} className='btn btn-primary'>Close</button>
                    </div>
                </div>
            </div>
            <div onClick={onClose} className='fixed z-[5] w-full h-full left-0 top-0 bg-bg-transparent backdrop-blur-[5px]'></div>
        </>
    );
}

// ── Main Page
export default function LeaveBalance() {
    const { user, hasRole, hasAnyPermission } = useAuth();
    const orgId = user?.orgId || user?.organization?.id;
    const myEmployeeId = user?.employeeId || user?.employee_id;
    const isPowerUser = user?.is_superuser
        || hasRole(['super_admin', 'admin', 'hr_admin', 'OPERATION_MANAGER'])
        || hasAnyPermission(['rbacapp.hr_view', 'rbacapp.hr_act', 'rbacapp.hr_approve']);


    const { data: leaveTypes = [] } = useLeaveTypes(orgId);

    const { data: allRecords = [], isLoading: isLoadingBalances } = useLeaveBalances(
        orgId,
        isPowerUser ? null : myEmployeeId,
        { enabled: isPowerUser }
    );

    const { data: dashboardData, isLoading: isLoadingDashboard } = useMeDashboard(
        !isPowerUser ? orgId : null
    );
    const myLeaveBalances = dashboardData?.leave_balances ?? [];

    const isLoading = isPowerUser ? isLoadingBalances : isLoadingDashboard;

    // Search state
    const [filterText, setFilterText] = useState('');
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

    // Modal state
    const [adjustModal, setAdjustModal] = useState({ open: false, employee: null });
    const [bulkSetModal, setBulkSetModal] = useState(false);
    const [historyModal, setHistoryModal] = useState(false);

    const seedMissing = useSeedMissingBalances();

    const handleSeed = () => {
        seedMissing.mutate({ orgId }, {
            onSuccess: (res) => toast.success(res?.message || 'Seeded missing balances'),
            onError: (err) => toast.error(err?.response?.data?.error || 'Failed to seed balances'),
        });
    };

    const subHeaderComponent = useMemo(() => {
        const handleClear = () => {
            if (filterText) {
                setResetPaginationToggle(!resetPaginationToggle);
                setFilterText('');
            }
        };
        return (
            <div className='flex gap-5 w-full justify-end'>
                <div className='relative w-full max-w-[221px]'>
                    <input
                        type='text'
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                        placeholder='Search here...'
                        className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none'
                    />
                </div>
                <button onClick={handleClear} className="btn btn-danger">Clear</button>
            </div>
        );
    }, [filterText, resetPaginationToggle]);

    // Flatten employees list for history filter dropdown
    const employeeList = useMemo(() => {
        const grouped = {};
        allRecords.forEach(r => {
            if (!grouped[r.employee]) {
                grouped[r.employee] = {
                    id: r.employee,
                    name: r.employee_info?.full_name || 'Unknown',
                };
            }
        });
        return Object.values(grouped);
    }, [allRecords]);

    // --- ADMIN/MANAGER TABLE DATA ---
    const tableData = useMemo(() => {
        if (!allRecords.length) return [];
        const grouped = allRecords.reduce((acc, curr) => {
            const empUuid = curr.employee;
            if (!acc[empUuid]) {
                acc[empUuid] = {
                    id: empUuid,
                    name: curr.employee_info?.full_name || 'Unknown',
                    title: curr.employee_info?.job_title || 'N/A',
                    dept: curr.employee_info?.department_name || 'N/A',
                    balances: []
                };
            }
            acc[empUuid].balances.push(curr);
            return acc;
        }, {});

        const search = filterText.toLowerCase();
        const filtered = Object.values(grouped).filter(emp =>
            !search ||
            emp.name.toLowerCase().includes(search) ||
            emp.dept.toLowerCase().includes(search) ||
            emp.title.toLowerCase().includes(search)
        );

        return filtered.map(emp => {
            const cells = {};
            leaveTypes.forEach(type => {
                const typeId = type.id || type.value;
                const record = emp.balances.find(b => b.leave_type === typeId);
                const avail = parseFloat(record?.balance_days || 0);
                const max = parseFloat(record?.leave_type_details?.max_days_per_year || 0);
                cells[typeId] = (
                    <div className='text-sm text-center'>
                        <span className='font-bold' style={{ color: 'var(--chart-color1)' }}>{avail.toFixed(1)}</span>
                        <span className='text-text-secondary'>/{max.toFixed(0)}</span>
                    </div>
                );
            });
            return {
                employee: (
                    <div className='flex items-center gap-10 py-5'>
                        {(() => {
                            const colors = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4'];
                            const initials = emp.name
                                ? emp.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                                : '?';
                            const bg = colors[(emp.name?.charCodeAt(0) || 0) % colors.length];
                            return (
                                <div style={{ backgroundColor: bg }} className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                    {initials}
                                </div>
                            );
                        })()}
                        <div>
                            <p className='font-medium text-sm leading-none mb-3'>{emp.name}</p>
                            <p className='text-[11px] text-text-secondary'>{emp.title}</p>
                        </div>
                    </div>
                ),
                department: <span className='text-sm'>{emp.dept}</span>,
                ...cells,
                _rawEmp: emp,
            };
        });
    }, [allRecords, leaveTypes, filterText]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="flex flex-col items-center gap-10">
                    <IconLoader2 size={32} className="animate-spin text-primary" />
                    <p className="text-sm text-text-secondary">Loading leave balances...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-20">
            <Breadcrumb title="Leave Balance" path="Leave / Balance" />

            {isPowerUser ? (
                <div className='bg-bg-dark rounded-xl border border-border shadow-light overflow-hidden'>
                    {/* Header */}
                    <div className='px-20 py-15 border-b border-border flex flex-wrap items-center justify-between gap-10'>
                        <div>
                            <h5 className='text-[15px] font-semibold'>All Employee Leave Balances</h5>
                            <p className='text-xs text-text-secondary mt-2'>{tableData.length} employee{tableData.length !== 1 ? 's' : ''}</p>
                        </div>
                        <div className='flex flex-wrap gap-8'>
                            <button
                                className='btn btn-outline-secondary flex items-center gap-6 text-sm'
                                onClick={() => setHistoryModal(true)}
                            >
                                <IconHistory size={15} />
                                History
                            </button>
                            <button
                                className='btn btn-outline-secondary flex items-center gap-6 text-sm'
                                onClick={handleSeed}
                                disabled={seedMissing.isPending}
                            >
                                <IconRefresh size={15} className={seedMissing.isPending ? 'animate-spin' : ''} />
                                Seed Missing
                            </button>
                            <button
                                className='btn btn-outline-secondary flex items-center gap-6 text-sm'
                                onClick={() => setBulkSetModal(true)}
                            >
                                <IconUsersGroup size={15} />
                                Bulk Set
                            </button>
                        </div>
                    </div>

                    <div className='react-data-table'>
                        <ReactDataTable
                            columns={[
                                { name: 'Employee', selector: row => row.employee, sortable: false, width: '230px' },
                                { name: 'Department', selector: row => row.department, sortable: true },
                                ...leaveTypes.map(t => ({
                                    name: t.name || t.label,
                                    selector: row => row[t.id || t.value],
                                    center: true,
                                    width: '120px',
                                })),
                                {
                                    name: 'Actions',
                                    center: true,
                                    width: '110px',
                                    cell: row => (
                                        <button
                                            className='btn btn-sm btn-outline-secondary flex items-center gap-5 text-xs'
                                            onClick={() => setAdjustModal({ open: true, employee: row._rawEmp })}
                                        >
                                            <IconAdjustments size={13} />
                                            Adjust
                                        </button>
                                    ),
                                },
                            ]}
                            data={tableData}
                            pagination
                            paginationResetDefaultPage={resetPaginationToggle}
                            subHeader
                            subHeaderComponent={subHeaderComponent}
                            highlightOnHover
                            noDataComponent={
                                <div className="flex flex-col items-center justify-center py-60">
                                    <p className="text-sm text-text-secondary">No leave balance records found.</p>
                                </div>
                            }
                        />
                    </div>
                </div>
            ) : (
                // ── EMPLOYEE VIEW ──
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-20'>
                    {myLeaveBalances.length === 0 ? (
                        <div className="col-span-2 text-center py-40 text-text-secondary text-sm">
                            No leave balances found. Contact HR.
                        </div>
                    ) : myLeaveBalances.map((lb, i) => {
                        const total = parseFloat(lb.allocated ?? 1);
                        const used = parseFloat(lb.used ?? 0);
                        const remaining = parseFloat(lb.available ?? (total - used));
                        const pct = Math.min(100, Math.round((used / total) * 100));
                        const color = COLORS[i % COLORS.length];
                        return (
                            <div key={i} className='bg-bg-dark rounded-xl border border-border p-20 shadow-light'>
                                <div className='flex items-start justify-between mb-15'>
                                    <div>
                                        <p className='text-[13px] font-semibold mb-3'>{lb.leave_type_name ?? lb.leave_type}</p>
                                        <p className='text-xs text-text-secondary'>{used} day{used !== 1 ? 's' : ''} used</p>
                                    </div>
                                    <div className='text-right'>
                                        <p className='text-[28px] font-bold leading-none' style={{ color }}>{remaining}</p>
                                        <p className='text-xs text-text-secondary mt-3'>of {total} days left</p>
                                    </div>
                                </div>
                                <div className='w-full bg-bg-light rounded-full h-6'>
                                    <div
                                        className='h-6 rounded-full transition-all duration-500'
                                        style={{ width: `${pct}%`, backgroundColor: color }}
                                    />
                                </div>
                                <div className='flex justify-between mt-8'>
                                    <span className='text-[10px] text-text-secondary'>0</span>
                                    <span className='text-[10px] text-text-secondary font-medium'>{pct}% used</span>
                                    <span className='text-[10px] text-text-secondary'>{total}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modals */}
            <AdjustBalanceModal
                isOpen={adjustModal.open}
                onClose={() => setAdjustModal({ open: false, employee: null })}
                employee={adjustModal.employee}
                leaveTypes={leaveTypes}
                orgId={orgId}
            />
            <BulkSetModal
                isOpen={bulkSetModal}
                onClose={() => setBulkSetModal(false)}
                leaveTypes={leaveTypes}
                orgId={orgId}
            />
            <HistoryModal
                isOpen={historyModal}
                onClose={() => setHistoryModal(false)}
                orgId={orgId}
                employees={employeeList}
                leaveTypes={leaveTypes}
            />
        </div>
    );
}
